import { Info } from "@phosphor-icons/react"
import { MenuDivider, Tooltip } from "@vesatogo/grass-core"
import { inr } from "@vesatogo/utils"
import { memo } from "react"
import { FormCard } from "~/components/FormCard"
import { TradeKindsReadable } from "~/constants/Kinds"

type PaymentProps = {
  finalTotal?: number
  supplierExpense?: number
  buyerExpense?: number
  sellerTdsAmount?: number
  supplierCharge?: string
  supplierSgst?: number
  supplierCgst?: number
  supplierIgst?: number
  buyerSgst?: number
  buyerCgst?: number
  buyerIgst?: number
  expenseSupplierSgst?: number
  expenseSupplierCgst?: number
  expenseSupplierIgst?: number
  expenseBuyerSgst?: number
  expenseBuyerCgst?: number
  expenseBuyerIgst?: number
  sellerPayable?: number
  buyerPayable?: number
  isDisabled?: boolean
  tradeAdvanceUtilized?: number
  advanceDetails?: any
}

type PaymentRowProps = {
  label: React.ReactNode
  value: React.ReactNode
  spacer?: boolean
}

const PaymentRow = memo(({ label, value, spacer = true }: PaymentRowProps) => (
  <div className="flex justify-between items-center py-2 px-4">
    <span className="text-gray-700 text-sm">{label}</span>
    <div className="flex items-center gap-4">
      {value}
      {spacer && <div className="w-[40px]" />}
    </div>
  </div>
))

const Payment = ({
  finalTotal = 0,
  supplierExpense = 0,
  buyerExpense = 0,
  sellerTdsAmount = 0,
  supplierCharge = "0",
  supplierSgst = 0,
  supplierCgst = 0,
  supplierIgst = 0,
  buyerSgst = 0,
  buyerCgst = 0,
  buyerIgst = 0,
  expenseSupplierSgst = 0,
  expenseSupplierCgst = 0,
  expenseSupplierIgst = 0,
  expenseBuyerSgst = 0,
  expenseBuyerCgst = 0,
  expenseBuyerIgst = 0,
  sellerPayable,
  buyerPayable,
  isDisabled = false,
  tradeAdvanceUtilized,
  advanceDetails,
}: PaymentProps) => {
  const totalSupplierGst = supplierIgst + supplierSgst + supplierCgst
  const totalBuyerGst = buyerIgst + buyerSgst + buyerCgst
  const totalExpenseSupplierGst =
    expenseSupplierIgst + expenseSupplierSgst + expenseSupplierCgst
  const totalExpenseBuyerGst =
    expenseBuyerIgst + expenseBuyerSgst + expenseBuyerCgst

  const supplierChargeNum = Number(supplierCharge ?? 0)
  const supplierExpenseWithGst = supplierExpense + totalExpenseSupplierGst
  const tradeTotalWithoutGST = finalTotal

  const liveSellerPayable =
    tradeTotalWithoutGST -
    supplierExpenseWithGst -
    supplierChargeNum -
    sellerTdsAmount -
    (tradeAdvanceUtilized || 0) +
    totalSupplierGst

  const liveBuyerPayable =
    tradeTotalWithoutGST + buyerExpense + totalExpenseBuyerGst + totalBuyerGst

  const computedSellerPayable =
    isDisabled && sellerPayable ? sellerPayable : liveSellerPayable

  const computedBuyerPayable =
    isDisabled && buyerPayable ? buyerPayable : liveBuyerPayable

  return (
    <FormCard title="Payment" childrenClassName="flex flex-col gap-0 text-sm">
      <PaymentRow
        label="Final Total (Excl. GST)"
        value={
          <span className="text-gray-900 font-600">
            {inr(tradeTotalWithoutGST)}
          </span>
        }
      />

      <PaymentRow
        label="Supplier Expense (Incl. Supplier Expense GST)"
        value={
          <span className="text-red font-600">
            {inr(supplierExpenseWithGst)}
          </span>
        }
      />

      <PaymentRow
        label="Supplier Charge"
        value={
          <span className="text-red font-600">{inr(supplierChargeNum)}</span>
        }
      />

      <PaymentRow
        label="Supplier TDS Value"
        value={
          <span className="text-red font-600">{inr(sellerTdsAmount)}</span>
        }
      />

      <PaymentRow
        label="Supplier Trade GST"
        value={
          <span className="text-gray-900 font-600">
            {inr(totalSupplierGst)}
          </span>
        }
      />

      {tradeAdvanceUtilized ? (
        <PaymentRow
          label={
            <div className="flex gap-1 items-center">
              Supplier Advance Utilized{" "}
              <Tooltip
                maxWidth="none"
                content={
                  <div className="min-w-max whitespace-nowrap">
                    {advanceDetails?.map((adv, index) => {
                      return (
                        <div
                          key={adv?.id ?? index}
                          className="flex items-center justify-between gap-6"
                        >
                          <span>
                            {`${
                              TradeKindsReadable[
                                adv?.parent_transaction?.advance_payment?.trade
                                  ?.kind
                              ]
                            } #${
                              adv?.parent_transaction?.advance_payment?.trade
                                ?.id
                            } | Adv id #${
                              adv?.parent_transaction?.advance_payment?.id
                            }`}
                          </span>
                          <span className="font-700">
                            {inr(adv?.amount || 0)}
                          </span>
                        </div>
                      )
                    })}
                    <MenuDivider className="border-blue-600" />
                    <div className="flex items-center justify-between gap-6">
                      <span>Total Amount Used:</span>
                      <span className="font-700">
                        {inr(tradeAdvanceUtilized || 0)}
                      </span>
                    </div>
                  </div>
                }
              >
                <Info
                  className="text-[#0677F2] cursor-pointer"
                  weight="fill"
                  size={20}
                />
              </Tooltip>
            </div>
          }
          value={
            <span className="text-gray-900 font-600">
              {inr(tradeAdvanceUtilized || 0)}
            </span>
          }
        />
      ) : null}
      <PaymentRow
        label="Buyer Expense"
        value={
          <span className="text-gray-900 font-600">{inr(buyerExpense)}</span>
        }
      />

      <PaymentRow
        label="Buyer Trade GST"
        value={
          <span className="text-gray-900 font-600">{inr(totalBuyerGst)}</span>
        }
      />

      <div className="border-t border-gray-200 mt-1">
        <div className="flex justify-between items-center py-2 px-4">
          <span>
            <span className="text-green font-600 text-sm">
              Supplier Payable Amount
            </span>{" "}
            <span className="text-gray-700 text-xs italic ml-1">
              (i.e. Final Total - Supplier Expense - Supplier Charge - Supplier
              TDS Value + Supplier Trade GST - Supplier Advance Utilized)
            </span>
          </span>
          <div className="flex items-center gap-4 text-sm">
            <span className="font-600 text-gray-900">
              {inr(computedSellerPayable)}
            </span>
            <div className="w-[40px]" />
          </div>
        </div>

        <div className="flex justify-between items-center py-2 px-4">
          <span>
            <span className="text-green font-600 text-sm">
              Buyer Receivable Amount
            </span>{" "}
            <span className="text-gray-700 text-xs italic ml-1">
              (i.e. Final Total + Buyer Expense + Expense GST + Buyer Trade GST)
            </span>
          </span>
          <div className="flex items-center gap-4 text-sm">
            <span className="font-600 text-gray-900">
              {inr(computedBuyerPayable)}
            </span>
            <div className="w-[40px]" />
          </div>
        </div>
      </div>
    </FormCard>
  )
}

export default Payment
